<template>
  <div v-loading="loading" class="application-status-container" :class="needHidden && 'hide'">
    <div class="title">
      <span class="title-text">{{ $t(`${i18nPrefix}.containerTitle.application.${dataType}`) }}</span>
    </div>
    <div class="status-number" :class="hasHoverStyle && 'hover-style'" @click="handleStatusNumberClick">{{ totalCount | formatBigNumber }}</div>
    <div ref="chart" class="candidates-chart"></div>
    <div class="mask"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import * as echarts from 'echarts'
import { mapActions, mapGetters } from 'vuex'
import mixin from './mixin'
import { READ_STATUS } from '@/constant/application'

const CHART_OPTION = {
  tooltip: {
    trigger: 'axis'
  },
  xAxis: {
    show: false,
    type: 'category',
    boundaryGap: false,
    data: []
  },
  yAxis: {
    show: false,
    type: 'value'
  },
  series: {
    type: 'line',
    data: [],
    symbol: 'circle',
    symbolSize: 2,
    color: '#659480',
    areaStyle: {
      color: '#e8f3ed'
    }
  },
  grid: {
    top: '30%',
    left: '0px',
    right: '0px',
    bottom: '0px'
  }
}

const DISABLE_LINK_LIST = ['LEADS']

export default {
  mixins: [mixin],
  props: {
    applicationData: {
      type: Object,
      required: true
    },
    showMyJobData: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    dataType: {
      type: String,
      required: true
    },
    timeRange: {
      type: Array,
      required: true
    },
    groupInfo: {
      type: Array,
      required: true
    },
    statusKey: {
      type: String,
      required: true
    },
    companyId: {
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.hrDashboard',
      loading: false,
      totalCount: 0,
      timedCount: [],
      selectTimeRange: '7',
      chart: null,
      chartPromise: null,
      chartPromiseResolve: null,
      chartResize: null
    }
  },
  computed: {
    needHidden () {
      if (this.dataType === 'LEADS' && !this.hasLeadsPermission) {
        return true
      }
      return false
    },
    hasHoverStyle () {
      if (_.includes(DISABLE_LINK_LIST, this.dataType)) {
        return false
      }
      return true
    }
  },
  created () {
    this.chartPromise = new Promise((resolve, reject) => {
      this.chartPromiseResolve = resolve
    })
    this.chartResize = _.debounce(() => this.chart.resize(), 100)
    this.refreshData()
  },
  mounted () {
    this.initChart()
    window.addEventListener('resize', this.chartResize)
  },
  destroyed () {
    echarts.dispose(this.chart)
    this.chart = null
    this.chartPromise = null
    this.chartPromiseResolve = null
    window.removeEventListener('resize', this.chartResize)
  },
  methods: {
    ...mapActions('hrDashboard', ['getApplicationCountInfo', 'changeApplicationSelected']),
    ...mapGetters('hrDashboard', ['applicationSelected']),
    initChart () {
      if (this.chart) {
        return
      }
      const chartEl = this.$refs.chart
      this.chart = echarts.init(chartEl)
      this.chart.setOption(CHART_OPTION)
      this.chartPromiseResolve()
    },
    async refreshData () {
      this.loading = true
      try {
        const dayList = []
        const countList = []
        const intervals = this.generateIntervals(this.timeRange[0], this.timeRange[1])
        this.totalCount = this.applicationData.count
        const timeData = this.applicationData.timeData
        _.forEach(intervals, (time) => {
          dayList.push(time)
          countList.push(timeData[time] || 0)
        })
        await this.chartPromise
        this.chart.setOption({
          xAxis: {
            data: dayList
          },
          series: {
            data: countList
          }
        })
      } finally {
        this.loading = false
      }
    },
    handleTimeRangeChange (timeRange) {
      this.selectTimeRange = timeRange
      this.refreshData()
      this.changeApplicationSelected({ name: this.dataType.toLowerCase(), value: timeRange })
    },
    generateIntervals (startDate, endDate) {
      const intervals = []
      const startTime = moment(startDate)
      const endTime = moment(endDate)
      // eslint-disable-next-line no-unmodified-loop-condition
      while (startTime <= endTime) {
        const format = startTime.format('YYYY-MM-DD')
        intervals.push(format)
        startTime.add(1, 'days')
      }
      return intervals
    },
    handleStatusNumberClick () {
      let url = '/v-admin/application'
      if (_.includes(DISABLE_LINK_LIST, this.dataType)) {
        return
      }

      const statusGroup = _.find(this.groupInfo, item => item.name.toLowerCase() === this.statusKey.toLowerCase())
      if (this.dataType === 'UNREAD') {
        const appliedDate = _.chain(this.timeRange).map(item => moment(item).format('YYYY-MM-DD')).join(',').value()
        url = `${url}?read_status=${READ_STATUS.UNREAD}&applied_date=${encodeURIComponent(appliedDate)}`
      } else if (statusGroup) {
        const statusList = _.map(statusGroup.status_list, item => item.key)
        const appliedDate = _.chain(this.timeRange).map(item => moment(item).format('YYYY-MM-DD')).join(',').value()
        url = `${url}?candidate_status=${encodeURIComponent(statusList)}&applied_date=${encodeURIComponent(appliedDate)}`
      } else {
        const appliedDate = _.chain(this.timeRange).map(item => moment(item).format('YYYY-MM-DD')).join(',').value()
        url = `${url}?applied_date=${encodeURIComponent(appliedDate)}`
      }

      if (this.companyId) {
        url = `${url}${_.includes(url, '?') ? '&' : '?'}company_id=${this.companyId}`
      }

      url = `${url}${_.includes(url, '?') ? '&' : '?'}my_job=${this.showMyJobData ? '1' : '0'}`

      window.open(url)
    }
  },
  watch: {
    isLoading (newVal) {
      if (!newVal) {
        this.refreshData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.application-status-container {
  display: flex;
  flex-direction: column;
  padding: 12px 18px 16px;
  box-shadow: 0px 2px 5px -3px rgba(0,0,0,0.12), 0px 5px 12px 0px rgba(0,0,0,0.08);

  &.hide {
    position: relative;
    box-shadow: unset;

    .mask {
      width: 100%;
      height: 100%;
      background: #FFFFFF;
      z-index: 1;
      top: 0;
      left: 0;
      position: absolute;
    }
  }

  .title {
    display: flex;
    justify-content: space-between;

    .title-text {
      font-weight: 500;
      line-height: 20px;
      color: #606266;
      font-size: 14px;
      height: 20px;

      i {
        margin-left: 5px;
      }
    }
    .title-right {
      .el-dropdown-link {
        font-weight: normal;
        font-size: 12px;
        cursor: pointer;
        .title-right-text{
          font-weight: normal;
        }
      }
    }
  }
  .status-number {
    font-size: 22px;
    height: 30px;
    color: #303133;
    line-height: 30px;
    font-weight: bold;
    margin: 0;
    width: max-content;

    &.hover-style {
      cursor: pointer;
      &:hover {
        color: #0096cc;
      }
    }
  }
  .candidates-chart {
    height: 60px;
  }
}

</style>
